import React, { useContext, useEffect, useState } from 'react';
import CreateProductContainer from 'views/CreateProductContainer';
import { Formik, FieldArray, FormikProps } from 'formik';
import { DashboardContainerColorProps } from 'enums/DashboardContainerEnums';
import ProductsService from 'services/ProductsService/products.service';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ConfirmationModalEnums,
  RawMaterialProps,
} from 'enums/RawMaterialEnums';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import { validationSchema } from './CreateProductFlowConstants';
import FlowContainer from 'views/FlowContainer';
import { Origin, rawMaterialPayload } from '../CreateProductFlowInterfaces';
import { handleTransformData, transformPayload } from './RawMaterialHelper';
import RequestIngredientModal from 'shared/components/RequestIngredientModal';
import COAlertModal from 'shared/components/COAlertModal';
import { HelpDeskContext } from 'context/HelpDeskContext';
import HelpdeskService from 'services/HelpdeskService/Helpdesk.service';
import isProductDisabled from 'shared/utils/isProductDisabled';
import FindLocationModal from 'shared/components/FindLocationModal';
import { customValidation } from './customValidate';
import IngredientList from './IngredientList';
import PackagingMaterialContainer from './PackagingMaterialContainer';
import { validateForHandleSave } from './IngredientList/validateForHandleSave';
import ConfirmationModal from './ConfirmationModal';
import { handleFormSubmission, handleSaveAsDraft } from './rawMaterialUtils';
import {
  showLocationModal,
  closeLocationModal,
  handleAddOrigins,
  handleRemoveOrigins,
  handleSaveLocationData,
  getSelectedIndexAddress,
} from './rawMaterialUtils';
import { defaultIngredientValue } from './defaultValue';
import Stage1AndStage2ProductService from 'services/ProductsService/stage1AndStage2Product.service';
import ProductCreationService from 'services/ProductsService/productCreation.service';

const CreateProductFlow: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const productId = Number(id);

  const { setProductData, setHelpDeskData } = useContext(HelpDeskContext);
  if (!productId) {
    navigate(AppRoutes.DASHBOARD);
  }
  const [requestIngredientModalState, setRequestIngredientModalState] =
    useState<boolean>(false);
  const [requestPackagingMaterialState, setRequestPackagingMaterialState] =
    useState<boolean>(false);
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] =
    useState<boolean>(false);
  const handleOpenRequestIngredientModal = () =>
    setRequestIngredientModalState(true);

  const handleCloseRequestIngredientModal = () =>
    setRequestIngredientModalState(false);

  const handleOpenRequestPackageMaterialModal = () =>
    setRequestPackagingMaterialState(true);

  const handleCloseRequestPackageMaterialModal = () =>
    setRequestPackagingMaterialState(false);

  const { fetchData, fetchedHelpDeskData } = HelpdeskService();
  const {
    saveAsDraftUpdateRawMaterial,
    saveAsDraftCreateRawMaterial,
    draftButtonLoading,
    buttonLoading,
  } = ProductsService();
  const { fetchSingleProduct: fetchChildProductData, newProduct } =
    ProductCreationService();
  const {
    updateRawMaterial,
    createProductRawMaterial,
    fetchIngredients,
    fetchPackagingMaterials,
    rawMateialPackagingMeta,
    rawMaterialIngredientsMeta,
    fetchRawMaterialListing,
    rawMaterialList,
  } = Stage1AndStage2ProductService();

  const [cancelModalState, setCancelModalState] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>();
  const [selectedType, setSelectedType] = useState<string>();
  const [selectedIndexAddress, setSelectedIndexAddress] = useState<Origin[]>(
    []
  );
  const [selectedRecordIndex, setSelectedRecordIndex] = useState<number>();
  const [selectedRecordType, setSelectedRecordType] = useState<string>();
  const [selectedOriginIndex, setSelectedOriginIndex] = useState<number>();

  useEffect(() => {
    fetchedHelpDeskData && setHelpDeskData(fetchedHelpDeskData);
  }, [fetchedHelpDeskData]);

  useEffect(() => {
    fetchIngredients();
    fetchPackagingMaterials();
    fetchChildProductData(productId);
    fetchRawMaterialListing(productId);
  }, []);
  useEffect(() => {
    setProductData(newProduct);
  }, []);

  useEffect(() => {
    localStorage.removeItem('stage1');
    localStorage.removeItem('stage2');
    localStorage.removeItem('stage3');
    localStorage.removeItem('stage4');
    localStorage.removeItem('stage1EmissionTotal');
    localStorage.removeItem('stage2EmissionTotal');
    localStorage.removeItem('stage3EmissionTotal');
    localStorage.removeItem('stage4EmissionTotal');
  }, []);

  const isDisabled = isProductDisabled(newProduct?.status);

  const handleFormSubmissionWrapper = async (payload: rawMaterialPayload) => {
    await handleFormSubmission(
      payload,
      isDisabled,
      navigate,
      id,
      productId,
      rawMaterialList,
      updateRawMaterial,
      createProductRawMaterial,
      transformPayload
    );
  };

  const fetchedTransformedData = handleTransformData(rawMaterialList);
  const initialValues: rawMaterialPayload = {
    ingredients:
      fetchedTransformedData && rawMaterialList.length > 0
        ? fetchedTransformedData.ingredients
        : [defaultIngredientValue],
    packageMaterials: fetchedTransformedData
      ? fetchedTransformedData.packageMaterials
      : [],
  };

  const handleSaveAsDraftWrapper = async (payload: rawMaterialPayload) => {
    await handleSaveAsDraft(
      payload,
      productId,
      rawMaterialList,
      saveAsDraftUpdateRawMaterial,
      saveAsDraftCreateRawMaterial,
      transformPayload
    );
  };

  const handleOpenCancelModal = () => setCancelModalState(true);

  const handleCloseCancelModal = (
    formikProps: FormikProps<rawMaterialPayload>
  ) => validateForHandleSave(formikProps, handleSaveAsDraftWrapper);

  const handleConfirmCancelModal = () =>
    navigate(`${AppRoutes.PRODUCT}/${AppRoutes.DRAFT}`);

  const handleCloseModal = () => setCancelModalState(false);

  const handleShowLocationModal = (
    index: number,
    type: string,
    selectedAddress?: Origin[],
    selectedRecordIndex?: number,
    selectedRecordType?: string,
    selectedOriginIndex?: number
  ) => {
    showLocationModal(
      index,
      type,
      setSelectedType,
      setSelectedIndex,
      setSelectedIndexAddress,
      setSelectedRecordIndex,
      setSelectedRecordType,
      setSelectedOriginIndex,
      selectedAddress,
      selectedRecordIndex,
      selectedRecordType,
      selectedOriginIndex
    );
  };

  const handleCloseLocationModal = (confirmation?: boolean) => {
    closeLocationModal(
      confirmation,
      setIsConfirmationModalVisible,
      setSelectedType,
      setSelectedIndex
    );
  };

  const handleAddOriginWrapper = (
    formikProps: FormikProps<rawMaterialPayload>,
    index: number,
    type?: string,
    id?: string
  ) => {
    handleAddOrigins(formikProps, index, type, id, isDisabled);
  };

  const handleRemoveOriginWrapper = (
    formikProps: FormikProps<rawMaterialPayload>,
    index: number,
    originIndex: number,
    type?: string
  ) => {
    handleRemoveOrigins(formikProps, index, originIndex, type);
  };

  const handleSave = (
    data: Origin[],
    formikProps: FormikProps<rawMaterialPayload>
  ) => {
    handleSaveLocationData(
      data,
      formikProps,
      selectedRecordType,
      selectedRecordIndex,
      handleCloseLocationModal
    );
  };

  const handleCloseConfirmation = () =>
    setIsConfirmationModalVisible(prev => !prev);

  const handleConfirmation = () => {
    handleCloseConfirmation();
    handleCloseLocationModal(false);
  };

  return (
    <FlowContainer bgColor={DashboardContainerColorProps.GREY}>
      {() => (
        <>
          <RequestIngredientModal
            showModalState={requestIngredientModalState}
            handleModalCancel={handleCloseRequestIngredientModal}
            handleModalConfirm={handleCloseRequestIngredientModal}
          />
          <RequestIngredientModal
            showModalState={requestPackagingMaterialState}
            handleModalCancel={handleCloseRequestPackageMaterialModal}
            handleModalConfirm={handleOpenRequestPackageMaterialModal}
            isPackageMaterial
          />
          <ConfirmationModal
            showModal={isConfirmationModalVisible}
            onCancel={handleCloseConfirmation}
            onConfirm={handleConfirmation}
            confirmationText={ConfirmationModalEnums.CONFIRMATION_TEXT}
            confirmButtonText={ConfirmationModalEnums.CONFIRM_BUTTON_TEXT}
            cancelButtonText={ConfirmationModalEnums.CANCEL_BUTTON_TEXT}
          />

          <CreateProductContainer>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              validate={values => customValidation(values)}
              onSubmit={handleFormSubmissionWrapper}>
              {formikProps => (
                <form onSubmit={formikProps.handleSubmit}>
                  <COAlertModal
                    handleModalCancel={() =>
                      handleCloseCancelModal(formikProps)
                    }
                    handleModalConfirm={handleConfirmCancelModal}
                    onModalCancel={handleCloseModal}
                    showModalState={cancelModalState}
                    alertModal
                    confirmLabel="Close anyway"
                    cancelLabel="Save & Proceed"
                    heading={
                      'Do you want to close the current page without saving?'
                    }
                  />

                  {selectedIndex !== undefined && (
                    <FindLocationModal
                      showModal={selectedIndex !== undefined}
                      selectedIndexData={selectedIndexAddress}
                      onSave={(data: Origin[]) => handleSave(data, formikProps)}
                      fetchInitialData={() =>
                        getSelectedIndexAddress(
                          formikProps,
                          selectedType,
                          selectedIndex
                        )
                      }
                      onCancel={() => handleCloseLocationModal(true)}
                      activeTabIndex={selectedOriginIndex}
                    />
                  )}

                  <FieldArray name={RawMaterialProps.INGREDIENTS}>
                    {arrayHelpers => (
                      <IngredientList
                        isDisabled={isDisabled}
                        arrayHelpers={arrayHelpers}
                        buttonLoading={buttonLoading}
                        handleOpenCancelModal={handleOpenCancelModal}
                        draftButtonLoading={draftButtonLoading}
                        handleSaveAsDraft={handleSaveAsDraftWrapper}
                        rawMaterialIngredientsMeta={rawMaterialIngredientsMeta}
                        handleOpenRequestIngredientModal={
                          handleOpenRequestIngredientModal
                        }
                        fetchData={fetchData}
                        showLocationModal={handleShowLocationModal}
                        handleAddOrigins={handleAddOriginWrapper}
                        handleRemoveOrigins={handleRemoveOriginWrapper}
                        newProduct={newProduct}
                      />
                    )}
                  </FieldArray>
                  {/* Packaging material conatiner */}
                  {formikProps.values.packageMaterials.length > 0 && (
                    <PackagingMaterialContainer
                      rawMateialPackagingMeta={rawMateialPackagingMeta}
                      handleOpenRequestPackageMaterialModal={
                        handleOpenRequestPackageMaterialModal
                      }
                      isDisabled={isDisabled}
                      fetchData={fetchData}
                      showLocationModal={handleShowLocationModal}
                      handleAddOrigins={handleAddOriginWrapper}
                      handleRemoveOrigins={handleRemoveOriginWrapper}
                      newProduct={newProduct}
                      formikProps={formikProps}
                    />
                  )}
                </form>
              )}
            </Formik>
          </CreateProductContainer>
        </>
      )}
    </FlowContainer>
  );
};

export default CreateProductFlow;
